import Component from '@cx-modules/match-facts';
import MatchFactsCss from '@cx-modules/match-facts/style.css?inline';
import { widgetBootstrap } from '../../services/cx-module-helper';
import css from '../../utils/css';
import { meta } from './meta.js';

export const attributes = meta.attributes;
export const widgetName = meta.name;

const {
  VITE_CLICKOUT_SERVICE_URL: clickoutServiceUrl,
  VITE_SPORTS_DATA_API_HOST: dataApiEndpoint
} = import.meta.env;

type Bookmaker = { bookmakerName?: string };

type ContractParams = {
  affIds?: unknown;
  apiKey?: unknown;
  analytics?: { getDataLayer: () => unknown } | false;
  bookies?: Record<string, Bookmaker>;
  bookmakersConfig?: Record<string, Bookmaker>;
  campaignName?: unknown;
  eventId?: unknown;
  countryCode?: string;
  subdivisionCode?: string;
  noDataAvailableFallbackText?: unknown;
  itemsPerPage?: unknown;
  oddsFormat?: unknown;
  marketType?: unknown;
  repub?: unknown;
  subeventId?: unknown;
  hash?: unknown;
  widgetStyle?: string;
  hideBets?: unknown;
};

type ContractProps = {
  apiKey?: unknown;
  affIds?: unknown;
  clickoutServiceUrl?: string;
  dataApiEndpoint?: string;
  oddsFormat?: unknown;
  eventId?: unknown;
  subeventId?: unknown;
  repub?: unknown;
  noDataAvailableFallbackText?: unknown;
  disableAnalytics: boolean;
  analyticsImpressionEventData: unknown;
  analyticsImpressionEventName: string;
  analyticsDataLayer?: unknown;
  countryCode?: string;
  subdivisionCode?: string;
  bookmakers: string[];
  pageSize?: unknown;
  theme: string;
  hideBets?: unknown;
};

export const contract = async ({
  affIds,
  apiKey,
  analytics,
  bookies,
  bookmakersConfig,
  campaignName,
  eventId,
  countryCode,
  subdivisionCode,
  noDataAvailableFallbackText,
  itemsPerPage,
  oddsFormat,
  marketType,
  repub,
  subeventId,
  hash: widgetSlot,
  widgetStyle,
  hideBets
}: ContractParams) => {
  const props: ContractProps = {
    apiKey,
    affIds,
    clickoutServiceUrl,
    dataApiEndpoint,
    oddsFormat,
    eventId,
    subeventId,
    repub,
    noDataAvailableFallbackText,
    disableAnalytics: !analytics,
    analyticsImpressionEventData: {
      bookieCode: Object.keys(bookmakersConfig || bookies || {}),
      bookieName: Object.values(bookmakersConfig || bookies || {}).map(
        b => b.bookmakerName
      ),
      campaignName,
      eventType: marketType,
      widgetType: 'match-facts',
      variation: 'paginated_list',
      widgetSlot
    },
    analyticsImpressionEventName: 'widget_impression',
    analyticsDataLayer: undefined,
    countryCode,
    subdivisionCode: undefined,
    bookmakers: Object.keys(bookmakersConfig || bookies || {}),
    pageSize: itemsPerPage,
    theme: widgetStyle || 'oddschecker',
    hideBets
  };
  if (analytics) {
    props.analyticsDataLayer = analytics.getDataLayer();
  }

  const countriesRequiringSubdivisionCode = ['US', 'CA'];
  const normalisedCountryCode = props.countryCode?.toUpperCase();

  if (normalisedCountryCode) {
    props.countryCode = normalisedCountryCode;
  }

  if (
    normalisedCountryCode &&
    countriesRequiringSubdivisionCode.includes(normalisedCountryCode)
  ) {
    const normalisedSubdivisionCode = (subdivisionCode ?? 'NJ')?.toUpperCase();
    props.subdivisionCode = normalisedSubdivisionCode;
  }
  return props;
};

const queryKey = ({
  bookmakersConfig,
  bookies,
  subeventId
}: {
  bookmakersConfig?: Record<string, Bookmaker>;
  bookies?: Record<string, Bookmaker>;
  subeventId?: unknown;
}) => {
  return [
    'match-facts',
    {
      bookmakers: Object.keys(bookmakersConfig || bookies || {}).join(','),
      subeventId
    }
  ];
};

widgetBootstrap({
  attributes,
  contract,
  Component,
  style: MatchFactsCss,
  name: widgetName,
  themeContract: css`
    #root > div {
      --mix-of-main-colors: color-mix(
        in lch,
        var(--widget-background-color) 85%,
        var(--widget-accent-background-color)
      );
      --mix-of-main-colors-2: color-mix(
        in lch,
        var(--widget-background-color) 50%,
        var(--widget-accent-background-color)
      );

      --match-facts-primary-backround: var(--widget-accent-background-color);
      --match-facts-primary-header: var(--widget-accent-text-color);

      --match-facts-fact-background: var(--widget-background-color);
      --match-facts-bet-background: var(--mix-of-main-colors);
      --match-facts-bet-color: var(--widget-text-color);

      --match-facts-fact-border: var(--widget-divider-color);
      --match-facts-font-family: var(--widget-font-family);
      --match-facts-pagination-button: var(--mix-of-main-colors-2);
      --match-facts-pagination-button-disabled: transparent;
      --match-facts-pagination-chevron: var(--widget-accent-text-color);
      --match-facts-pagination-chevron-disabled: #6666;
      --match-facts-pagination-text: var(--match-facts-pagination-chevron);
      --match-facts-loading-placeholder-color: var(--widget-text-color);
      --match-facts-template-highlight-color: var(
        --widget-cta-background-color
      );
      --match-facts-bet-btn-width: 86px;
      --match-facts-bet-btn-height: 34px;

      --match-facts-error-message: var(--widget-accent-text-color);

      --match-facts-bet-btn-text: var(--widget-cta-color);
      --match-facts-bet-btn-background: var(--widget-cta-background-color);
      --match-facts-bet-btn-hover-background: var(--widget-cta-hover-color);

      border-radius: var(--widget-border-radius, 8px);

      /**
       * mix cta-background-color with 16% of text-color,
       * so its more readable for
       * * lighter cta-background on light backgrounds and
       * * darker cta-background on dark backgrounds
       *
       * apply only for factTemplate
       */

      [class*='_factTemplate_'] {
        --match-facts-template-highlight-color: color-mix(
          in srgb,
          var(--widget-cta-background-color),
          var(--widget-text-color) 16%
        );
      }

      [class*='_pagination_'] [class*='_buttonGroup_'] button {
        border-radius: var(--widget-cta-border-radius, 4px);
      }

      [class*='_facts_'] > [class*='_fact_'] {
        border-radius: var(--widget-inner-border-radius, 8px);
        overflow: clip;

        [class*='_fact_'],
        [class*='_bet_'] {
          border-radius: 0;
        }
      }

      [class*='_oddsButton_'] {
        color: var(--match-facts-bet-btn-text);
        border-radius: var(--widget-cta-border-radius, 4px);

        [class*='_bookmakerLogo_'] {
          border-radius: var(--widget-cta-border-radius, 4px) 0 0
            var(--widget-cta-border-radius, 4px);
        }

        &:not(:hover) {
          background: var(--match-facts-bet-btn-background);
        }
      }
    }
  `,
  queryKey,
  version: '0.1.1' // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
});
